/* eslint-disable  @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useEffect, useState } from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import {initialAnalyticsState } from '@context/analyticsReducer';
import { useAnalyticsContext } from '@context/analyticsContext';
import i18next from 'utils/i18n';
import Logger from 'utils/logger';
import { getContactData } from '@services/utils';
import { useFormContext } from '@context/FormContext';
import { UNIDENTIFIED_SOURCE } from '@shared/constants/utm';
import { convertKeysToSnakeCase } from '../../utils/stringFormat';

const dateTimeNow = new Date().toISOString();
const analytics = AnalyticsBrowser.load({ writeKey: process.env.REACT_APP_SEGMENT_KEY as string });
const logger = new Logger('CDXSegmentAnalytics');
const isWidget = process.env.REACT_APP_IS_WIDGET;

export type NestedObject = {
  [key: string]: string | number | boolean | null | undefined | NestedObject | NestedObject[];
};

// analytics.ready().then(() => {
//   posthog.init(process.env.REACT_APP_POSTHOG_KEY as string, {
//     api_host: 'https://us.i.posthog.com',
//     capture_pageview: false,
//     loaded: () => {
//       logger.log('PostHog loaded and connected to Segment');
//     },
//   });
// });

export const useAnalytics = (screenParams = {}, isTrackAbandon = true) => {
  const { widgetInitVariables } = useFormContext();
  const {dispatch}= useAnalyticsContext();
  const contactData = getContactData();
  const stateRef = useRef(initialAnalyticsState);
  const parsedUTM = JSON.parse(widgetInitVariables?.tracking?.extraData || '{}');
  const deviceType = window.innerWidth <= 500 ? 'mobile' : 'desktop';

  type TimeoutId = ReturnType<typeof setTimeout> | null;

  const renderTimeRef = useRef(Date.now());
  const interactionStartedRef = useRef(false);
  const interactionStartTimeRef = useRef<number | null>(null);
  const idleTimerRef = useRef<TimeoutId>(null);
  const abandonedRef = useRef(false);
  const pageExitTrackedRef = useRef(false);
  const [isIdle, setIsIdle] = useState(false);

  const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

  const extraParameters = {
    // Core widget identification
    widget_type: 'CDX',
    widget_location: parsedUTM?.widget_location, // 'hero-cyoa' or 'hero-ecalc'
    widget_name: 'CDX',

    // Session/User identification
    anonymousId: parsedUTM?.anonymous_id,
    $session_id: parsedUTM?.session_id,
    partner_name: widgetInitVariables?.PartnerName || parsedUTM?.partner_name || UNIDENTIFIED_SOURCE,

    // UTM parameters (merge all from parsedUTM)
    utm_source: parsedUTM?.utm_source,
    utm_medium: parsedUTM?.utm_medium,
    utm_campaign: parsedUTM?.utm_campaign,
    utm_term: parsedUTM?.utm_term,
    utm_content: parsedUTM?.utm_content,
    gclid: parsedUTM?.gclid,
    partnerdesc: parsedUTM?.partnerdesc,
    irclickid: parsedUTM?.irclickid,
    subid1: parsedUTM?.subid1,
    subid2: parsedUTM?.subid2,
    subid3: parsedUTM?.subid3 || deviceType,
    subid: parsedUTM?.subid,
    sharedid: parsedUTM?.sharedid,
    clickid: parsedUTM?.clickid,
    arm_enabled: parsedUTM?.arm_enabled,
    search_id: parsedUTM?.search_id,
    secondary_source: parsedUTM?.secondary_source,
    // Lead/Application details
    loan_application_id: parsedUTM?.loan_application_id || '',
    leadId: contactData?.leadId,
    // Page context
    page_url: parsedUTM?.page_url,
    page_type: 'landing_page',
    referrer: parsedUTM?.referrer,
    timestamp: dateTimeNow,
    widget_version: '3.0.0',
  };

  const identifierObject = contactData?.contactId
    ? {
        userId: contactData?.contactId,
      }
    : {
        anonymousId: parsedUTM?.anonymous_id,
      };

 
  // Calculate time spent on form (only after interaction has started)
  const getTimeSpentMs = useCallback(() => {
    if (!interactionStartTimeRef.current) {
      return 0;
    }
    return Date.now() - interactionStartTimeRef.current;
  }, []);

  const startInteraction = useCallback(() => {
    if (!interactionStartedRef.current) {
      interactionStartedRef.current = true;
      interactionStartTimeRef.current = Date.now();
      const timeSpentbeforeInteractionMs = Date.now() - renderTimeRef.current;
      if (Object.keys(screenParams).length > 0) {
        try {
          const parametersWithUtm = {
            ...extraParameters,
            ...screenParams,
            time_spent_ms: timeSpentbeforeInteractionMs,
          };
          analytics.track('Interaction Started', parametersWithUtm, identifierObject);
          logger.log('Analytics Interaction started ->', {
            timeSpentbeforeInteractionMs,
          });
        } catch (error) {
          logger.log('User interaction started');
        }
      }
      resetIdleTimer();
    }
  }, [extraParameters, getTimeSpentMs, screenParams]);

  // Send abandon event with time tracking (only if interaction started)
  const trackAbandonEvent = useCallback(
    (abandonReason: string, additionalParams?: any) => {
      if (!interactionStartedRef.current) {
        logger.log('Abandon event not tracked - interaction never started');
        return;
      }

      const timeSpentMs = getTimeSpentMs();
      const abandonParameters = {
        ...extraParameters,
        ...additionalParams,
        abandon_reason: abandonReason,
        time_spent_ms: timeSpentMs,
      };
      if (Object.keys(screenParams).length > 0 && isTrackAbandon) {
        try {
          const parametersWithUtm = {
            ...abandonParameters,
            ...extraParameters,
            ...screenParams,
          };
          analytics.track('Form Abandoned', parametersWithUtm, identifierObject);
          logger.log('Analytics abandonTrack ->', {
            reason: abandonReason,
            timeSpentMs,
          });
        } catch (error) {
          logger.log('Analytics abandonTrack error ->', abandonReason, error);
        }
      }
    },
    [extraParameters, getTimeSpentMs, screenParams, isTrackAbandon]
  );

  // Reset idle timer on user activity
  const resetIdleTimer = useCallback(() => {
    if (!interactionStartedRef.current) {
      return;
    }

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    setIsIdle(false);

    idleTimerRef.current = setTimeout(() => {
      if (!abandonedRef.current) {
        abandonedRef.current = true;
        trackAbandonEvent('idle_timeout', { idle_duration_ms: IDLE_TIMEOUT });
        setIsIdle(true);
      }
    }, IDLE_TIMEOUT);
  }, [trackAbandonEvent]);

  // Initialize time tracking and listeners
  useEffect(() => {
    renderTimeRef.current = Date.now();
    abandonedRef.current = false;
    pageExitTrackedRef.current = false;

    // Listen for user activity to reset idle timer (only if interaction started)
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'input', 'change'];
    const handleActivity = () => {
      if (interactionStartedRef.current) {
        resetIdleTimer();
      }
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Listen for tab/window close (not page refresh)
    const handleBeforeUnload = () => {
      if (pageExitTrackedRef.current) {
        return;
      }

      if (!abandonedRef.current && interactionStartedRef.current) {
        // Check if it's a page refresh or actual tab close
        const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const isRefresh = perfData?.type === 'reload';

        // Only track tab close, not refresh
        if (!isRefresh) {
          pageExitTrackedRef.current = true;
          abandonedRef.current = true;
          trackAbandonEvent('page_exit');
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [resetIdleTimer, trackAbandonEvent]);



  const trackPage = useCallback(
    (pageTitle: string, parameters?: any) => {
      if (isWidget && isWidget !== 'true') {
        document.title = `${pageTitle} | ${i18next.t('pageTitles.suffix')}`;
      }
      const parametersWithUtm = {
        ...screenParams,
        ...parameters,
        ...extraParameters,
      };
      try {
        analytics.track('Screen Viewed', parametersWithUtm, identifierObject);
        logger.log('Analytics trackPage ->', pageTitle);
      } catch (error) {
        logger.log('Analytics trackPage error ->', pageTitle, error);
      }
      dispatch({ type: 'TRACK_PAGE', payload: { eventType: pageTitle, parameters: parametersWithUtm } });
    },
    [dispatch, extraParameters, getTimeSpentMs, screenParams, identifierObject]
  );



  const trackClick = useCallback(
    (eventType: string, parameters?: any) => {
      const formatParameters = convertKeysToSnakeCase(parameters);
      const parametersWithUtm = {
        ...screenParams,
        ...formatParameters,
        ...extraParameters,
        time_spent_ms: getTimeSpentMs(),
      };
      try {
        analytics.track(eventType, parametersWithUtm, identifierObject);
        logger.log('Analytics trackClick ->', eventType, parametersWithUtm ?? '');
      } catch (error) {
        logger.log('Analytics trackClick error ->', eventType, error);
      }
    },
    [dispatch, extraParameters, getTimeSpentMs, screenParams, identifierObject]
  );

 

  const trackSystemError = useCallback(
    (eventType: string, parameters?: any) => {
      const formatParameters = convertKeysToSnakeCase(parameters);
      const parametersWithUtm = {
        ...screenParams,
        ...formatParameters,
        ...extraParameters,
        time_spent_ms: getTimeSpentMs(),
      };
      try {
        analytics.track(eventType, parametersWithUtm, identifierObject);
        logger.log('Analytics trackSystemError ->', eventType, parametersWithUtm);
      } catch (error) {
        logger.log('Analytics trackSystemError error ->', eventType, parametersWithUtm, error);
      }
      dispatch({
        type: 'TRACK_SYSTEM_ERROR',
        payload: { eventType, parameters: parametersWithUtm },
      });
    },
    [dispatch, extraParameters, getTimeSpentMs, widgetInitVariables, screenParams, identifierObject]
  );


  const identifyUser = useCallback(
    (id: string, formData: any) => {
      try {
        analytics.identify(id, {
          ...formData,
          anonymousId: parsedUTM?.anonymous_id,
          $session_id: parsedUTM?.session_id,
          time_spent_ms: getTimeSpentMs(),
        });

        logger.log('Analytics identifyUser ->', formData);
      } catch (error) {
        logger.log('Analytics identifyUser error ->', formData, error);
      }
      dispatch({
        type: 'IDENTIFY_USER',
        payload: { eventType: id, parameters: formData },
      });
    },
    [dispatch, getTimeSpentMs]
  );

  // Reset tracking for new form instance
  const resetTracking = useCallback(() => {
    interactionStartedRef.current = false;
    interactionStartTimeRef.current = null;
    abandonedRef.current = false;
    pageExitTrackedRef.current = false;
    setIsIdle(false);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
  }, []);

  // Mark form as abandoned manually
  const markFormAbandoned = useCallback(
    (reason = 'user_action', additionalParams?: any) => {
      if (!abandonedRef.current && interactionStartedRef.current) {
        abandonedRef.current = true;
        trackAbandonEvent(reason, additionalParams);
      }
    },
    [trackAbandonEvent]
  );



  const trackFormSubmit = useCallback(
    (eventType: string, parameters?: any, isReset = false) => {
      // Mark as submitted to prevent abandon event
      abandonedRef.current = true;
      const formatParameters = convertKeysToSnakeCase(parameters);
      const timeSpentMs = getTimeSpentMs();
      const parametersWithUtm = {
        ...screenParams,
        ...formatParameters,
        ...extraParameters,
        time_spent_ms: timeSpentMs,
      };
      try {
        analytics.track(eventType, parametersWithUtm, identifierObject);
        logger.log('Analytics trackFormSubmit ->', eventType, parametersWithUtm);
      } catch (error) {
        logger.log('Analytics trackFormSubmit error ->', eventType, parametersWithUtm, error);
      }
      isReset && resetTracking();
    },
    [extraParameters, getTimeSpentMs, resetTracking, screenParams]
  );

  return {
    startInteraction,
    trackPage,
    trackClick,
    trackFormSubmit,
    trackSystemError,
    identifyUser,
    markFormAbandoned,
    getTimeSpentMs,
    resetTracking,
    isIdle,
    state: stateRef.current,
  };
};
